<template>
  <AnchoredDrawer v-model:open="isOpen" :title="title">
    <p class="confirm-drawer__message">{{ message }}</p>
    <!-- A failed action answers inside the drawer the person is already in; the
         confirm control below is then the retry. -->
    <p v-if="error" class="confirm-drawer__error" role="alert">{{ error }}</p>
    <template #footer>
      <div class="confirm-drawer__actions">
        <button class="confirm-drawer__btn confirm-drawer__btn--ghost" type="button" :disabled="busy" @click="onCancel">
          {{ cancelLabel }}
        </button>
        <button
          class="confirm-drawer__btn"
          :class="destructive ? 'confirm-drawer__btn--danger' : 'confirm-drawer__btn--primary'"
          type="button"
          :disabled="busy"
          @click="onConfirm"
        >{{ confirmLabel }}</button>
      </div>
    </template>
  </AnchoredDrawer>
</template>

<script setup>
// The desktop "are you sure?" — the twin of the mobile ConfirmSheet, composed
// on AnchoredDrawer so a destructive action confirms inside the panel or card
// it belongs to, never through the browser's native confirm() popup. Dumb: the
// caller owns the copy and what happens on confirm.
import AnchoredDrawer from '../AnchoredDrawer/AnchoredDrawer.vue';

defineOptions({ name: 'ConfirmDrawer' });

const isOpen = defineModel('open', { type: Boolean, default: false });

const props = defineProps({
  title: { type: String, default: '' },
  message: { type: String, default: '' },
  confirmLabel: { type: String, required: true },
  cancelLabel: { type: String, required: true },
  // Paints the confirm button red — for a permanent action (close account).
  destructive: { type: Boolean, default: false },
  // Off when the caller's action can fail: confirming then leaves the drawer
  // open, the caller closes it (v-model:open) on success or sets `error` on
  // failure, and the confirm control becomes the retry. On by default so a
  // fire-and-forget confirm still closes itself.
  closeOnConfirm: { type: Boolean, default: true },
  // The action is running: both controls disable so a second click cannot run
  // it twice.
  busy: { type: Boolean, default: false },
  // What went wrong, in the caller's words, shown inside the drawer under the
  // message. Empty means nothing went wrong (yet).
  error: { type: String, default: '' },
});

const emit = defineEmits(['confirm', 'cancel']);

function onConfirm() {
  if (props.closeOnConfirm) isOpen.value = false;
  emit('confirm');
}

function onCancel() {
  isOpen.value = false;
  emit('cancel');
}
</script>

<style scoped>
.confirm-drawer__message {
  margin: 0;
  font-size: var(--p-confirm-drawer-message-font-size);
  line-height: var(--p-confirm-drawer-message-line-height);
  color: var(--p-confirm-drawer-message-color);
}

.confirm-drawer__error {
  margin: 10px 0 0;
  font-size: var(--p-confirm-drawer-error-font-size);
  line-height: var(--p-confirm-drawer-error-line-height);
  color: var(--p-confirm-drawer-error-color);
}

/* Desktop order: the primary action sits at the right end. */
.confirm-drawer__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--p-confirm-drawer-actions-gap);
}

.confirm-drawer__btn {
  height: 40px;
  border: none;
  border-radius: var(--p-confirm-drawer-btn-border-radius);
  padding: var(--p-confirm-drawer-btn-padding);
  font-family: inherit;
  font-size: var(--p-confirm-drawer-btn-font-size);
  font-weight: var(--p-confirm-drawer-btn-font-weight);
  cursor: pointer;
}

.confirm-drawer__btn:disabled {
  opacity: 0.6;
  cursor: progress;
}

.confirm-drawer__btn--primary {
  background: var(--p-confirm-drawer-btn-primary-background);
  color: var(--p-confirm-drawer-btn-primary-color);
}

.confirm-drawer__btn--danger {
  background: var(--p-confirm-drawer-btn-danger-background);
  color: var(--p-confirm-drawer-btn-danger-color);
}

.confirm-drawer__btn--ghost {
  background: var(--p-confirm-drawer-btn-ghost-background);
  color: var(--p-confirm-drawer-btn-ghost-color);
  border: var(--p-confirm-drawer-btn-ghost-border-width) solid var(--p-confirm-drawer-btn-ghost-border-color);
}
</style>
